import {
  Box,
  Card,
  CardContent,
  List,
  ListItemButton,
  ListItemText,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import { useLanguage } from '../../../hooks';
import { DataListSource } from '../../../types';
import { LocationSelectorContent } from '../../Locations/LocationsSelector/LocationSelectorContent';
import { LocationsState } from '../../Locations/LocationsSelector/LocationsSelectorReducer';
import { InfoAlert } from '../../uiComponents/infoAlert';
import { Category, ListTabs } from '../searchDataListModal';

const customStyles = {
  listItem: {
    paddingTop: '2px',
    paddingBottom: '2px',
    paddingLeft: '4px',
    paddingRight: '4px',
    wordBreak: 'break-word',
  },
  listTitle: {
    fontSize: '16px',
    fontWeight: '600px',
    margin: '0px',
  },
  listContainer: {
    '&:nth-child(1)': {
      marginTop: '8px',
    },
    '&:nth-child(2)': {
      marginTop: '16px',
    },
    marginTop: '16px',
  },
  locationsContainer: {
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
  },
  tabList: {
    borderBottom: '1px solid #E0E0E0',
  },
  categoryCard: {
    cursor: 'pointer',
    border: '1px solid #E0E0E0',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.1s ease-in-out',
    '&:hover': {
      borderColor: '#3E8FFA',
      boxShadow: '0 4px 12px rgba(62, 143, 250, 0.15)',
    },
  },
  categoryCardContent: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    textAlign: 'center',
    gap: '12px',
  },
  categoryDataContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingLeft: '16px',
    gap: '10px',
  },
  categoryTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#3B3C40',
    margin: 0,
  },
  categoryDescription: {
    fontSize: '14px',
    color: '#6B6D76',
    lineHeight: '20px',
    margin: 0,
    textAlign: 'left',
  },
  categoryList: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '24px',
    marginBottom: '20px',
    gap: '16px',
  },
  categoryInstructions: {
    fontSize: '16px',
    color: '#6B6D76',
    marginBottom: '16px',
  },
} satisfies Record<string, SxProps<Theme>>;

interface SearchDataListModalListProps {
  sourcesList: DataListSource[];
  sourcesInForm: DataListSource[];
  onSelect: (value: string) => void;
  selected: string;
  searchQuery: string;
  locationsState: LocationsState;
  handleChangeLocationSelection: (
    locationId: number,
    locationType: 'NestableLocation' | 'Location',
  ) => void;
  handleChangeAllLocationsSelection: () => void;
  activeTab: ListTabs | null;
  setActiveTab: (tab: ListTabs | null) => void;
  anyLocationSelected: boolean;
  handleConfirm: (selectedValue?: string) => void;
  categories: Category[];
}

export const SearchDataListModalList = ({
  sourcesList,
  onSelect,
  selected,
  sourcesInForm,
  searchQuery,
  locationsState,
  handleChangeLocationSelection,
  handleChangeAllLocationsSelection,
  activeTab,
  setActiveTab,
  anyLocationSelected,
  handleConfirm,
  categories,
}: SearchDataListModalListProps) => {
  const lang = useLanguage();

  const renderList = (list: DataListSource[]) => {
    return list.map(({ tag, value }) => (
      <ListItemButton
        key={value}
        sx={customStyles.listItem}
        onClick={() => onSelect(value)}
        selected={selected === value}
      >
        <ListItemText primary={tag} />
      </ListItemButton>
    ));
  };

  const handleCategoryClick = (categoryId: ListTabs) => {
    if (categoryId === ListTabs.CustomList || categoryId === ListTabs.Locations) {
      setActiveTab(categoryId);
    }

    /* When assets sublists are implemented, change the active tab to ASSETS */
    if (categoryId === ListTabs.Assets) {
      onSelect('assets');
      handleConfirm('assets');
    }
  };

  if (!activeTab) {
    return (
      <Box>
        <Typography
          variant='h3'
          sx={customStyles.categoryInstructions}
        >
          {lang.form_builder.selectDataListInstruction}
        </Typography>
        <Box sx={customStyles.categoryList}>
          {categories.map((category) => (
            <Card
              key={category.id}
              sx={customStyles.categoryCard}
              onClick={() => handleCategoryClick(category.id)}
            >
              <CardContent sx={customStyles.categoryCardContent}>
                {category.icon}
                <Box sx={customStyles.categoryDataContainer}>
                  <Typography sx={customStyles.categoryTitle}>{category.title}</Typography>
                  <Typography sx={customStyles.categoryDescription}>
                    {category.description}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <>
      {activeTab === ListTabs.CustomList && (
        <>
          {sourcesInForm.length > 0 && (
            <Box sx={customStyles.listContainer}>
              <Typography
                variant='h1'
                sx={customStyles.listTitle}
              >
                {lang.form_builder.usedInThisForm}
              </Typography>
              <List>{renderList(sourcesInForm)}</List>
            </Box>
          )}
          {sourcesList.length > 0 && (
            <Box sx={customStyles.listContainer}>
              <Typography
                variant='h1'
                sx={customStyles.listTitle}
              >
                {lang.form_builder.otherLists}
              </Typography>
              <List>{renderList(sourcesList)}</List>
            </Box>
          )}
        </>
      )}
      {activeTab === ListTabs.Locations && (
        <>
          <Box sx={customStyles.locationsContainer}>
            {anyLocationSelected && <InfoAlert message={lang.form_builder.locationAccessAlert} />}
          </Box>
          <LocationSelectorContent
            selectedLocations={locationsState.selectedLocations}
            searchQuery={searchQuery}
            onSelect={(locationId) => handleChangeLocationSelection(locationId, 'NestableLocation')}
            allLocationsSelected={locationsState.allLocationsSelected}
            onSelectAll={handleChangeAllLocationsSelection}
            showAltEmptyState={false}
            multipleSelection
          />
        </>
      )}
    </>
  );
};
